import { useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as CheckIcon } from '../../../../assets/icons/check.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/icon-download.svg';
import CustomButton from '../../../../shared/components/Buttons/CustomButton/CustomButton';
import { Toast } from '../../../../shared/components/Globals/Toast/Toast';
import CustomModal from '../../../../shared/components/Modals/CustomModal/CustomModal';
import { CLICK_ACTION, DOWNLOAD_PDF_QUOTE, SOURCE_MODAL } from '../../../../shared/constants/tagManager';
import { useDownloadQuotePdf } from '../../../../shared/hooks/useDownloadQuotePdf/useDownloadQuotePdf';
import { useUser } from '../../../../shared/hooks/useUser/useUser';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_QUOTES } from '../../../../shared/translations/quotes';
import './QuoteSuccessModal.scss';

interface IQuoteSuccessModalProps {
  open: boolean;
  quoteName: string;
  companyName: string;
  quoteId: number | null;
  prospectId: number;
  mode?: 'create' | 'update';
  onClose: () => void;
}

const QuoteSuccessModal = ({
  open,
  quoteName,
  companyName,
  quoteId,
  prospectId,
  mode = 'update',
  onClose
}: IQuoteSuccessModalProps) => {
  const { t } = useTranslation();
  const { brokerId } = useUser();
  const { downloadAndOpenPdf, downloadingQuoteId, cancel } = useDownloadQuotePdf();
  const [pdfError, setPdfError] = useState(false);
  const translationGroup =
    mode === 'create' ? TRANSLATIONS_QUOTES.create.createSuccessModal : TRANSLATIONS_QUOTES.create.editSuccessModal;

  const handleClose = () => {
    cancel();
    onClose();
  };

  const handleDownloadPdf = async () => {
    TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_PDF_QUOTE, SOURCE_MODAL);
    if (!brokerId || quoteId === null) return;

    const outcome = await downloadAndOpenPdf(brokerId, prospectId, quoteId);
    if (outcome === 'aborted') return;
    if (outcome === 'failed') {
      setPdfError(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <CustomModal open={open} size="m">
        <div className="quote-edit-success-modal">
          <div className="quote-edit-success-modal__icon-wrapper">
            <CheckIcon className="quote-edit-success-modal__icon" aria-hidden="true" />
          </div>
          <h2 className="quote-edit-success-modal__title">{t(translationGroup.title, { quoteName })}</h2>
          <p className="quote-edit-success-modal__description">{t(translationGroup.description, { companyName })}</p>
          <div className="quote-edit-success-modal__actions">
            <CustomButton variant="default" onClick={handleClose}>
              {t(translationGroup.doItLater)}
            </CustomButton>

            <CustomButton
              variant="primary"
              loading={downloadingQuoteId !== null}
              loadingText={t(translationGroup.downloading)}
              onClick={handleDownloadPdf}
              disabled={downloadingQuoteId !== null}
            >
              {t(translationGroup.downloadPdf)}
              <DownloadIcon />
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      <Toast
        open={pdfError}
        message={<Trans i18nKey={translationGroup.downloadPdfError} components={{ b: <b /> }} />}
        variant="warning"
        onClose={() => setPdfError(false)}
      />
    </>
  );
};

export default QuoteSuccessModal;
